/**
 * PDF Generation Cache
 * 
 * Caches generated HTML for PDFs to avoid regenerating unchanged content.
 * Cache is invalidated when quote data changes.
 */

interface CacheEntry {
  html: string;
  timestamp: number;
  version: number; // Quote version or hash
}

const cache = new Map<string, CacheEntry>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Generate a cache key for a quote/document
 */
export const getCacheKey = (
  type: 'quote' | 'po' | 'invoice' | 'schedule' | 'service-invoice' | 'service-report',
  id: string,
  version?: number
): string => {
  return `${type}:${id}:${version || 0}`;
};

/**
 * Get cached HTML if available and valid
 */
export const getCachedHTML = (key: string): string | null => {
  const entry = cache.get(key);
  
  if (!entry) {
    return null;
  }
  
  // Check if cache expired
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  
  return entry.html;
};

/**
 * Cache generated HTML
 */
export const cacheHTML = (key: string, html: string, version?: number): void => {
  cache.set(key, {
    html,
    timestamp: Date.now(),
    version: version || 0
  });
};

/**
 * Invalidate cache for a specific document
 */
export const invalidateCache = (key: string): void => {
  cache.delete(key);
};

/**
 * Invalidate all caches for a quote (all related documents)
 */
export const invalidateQuoteCache = (quoteId: string): void => {
  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.includes(quoteId)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => cache.delete(key));
};

/**
 * Clear all cached PDFs
 */
export const clearCache = (): void => {
  cache.clear();
};

/**
 * Get cache statistics
 */
export const getCacheStats = (): { size: number; entries: string[] } => {
  return {
    size: cache.size,
    entries: Array.from(cache.keys())
  };
};
